import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { CircuitBoard, User, UserCheck, Users } from "lucide-react";
import { icon, label } from "../../../../config";
import NodeDeleteConfirmPopup from "./NodeDeleteConfirmPopup";
import { getDD4List } from "../../../TaskSidebar/TaskSidebarContent/Details/taskDetail.helper";
import "./AssignmentDisplay.scss";

const AssignmentDisplay = ({ setInEdit, onDeleteNode, nodeId, assignmentData, isReadOnly = false, isCompleted = false }) => {
  const { defaultDetails } = useSelector((state) => state.tasks);
  const [enrichedData, setEnrichedData] = useState(null);
  const [showDeleteConfirmationPopup, setShowDeleteConfirmationPopup] = useState(false);
  const deleteIconRef = useRef(null);
  const deleteRef = useRef(null);
  const blurTimeoutRef = useRef(null);

  const handleDelete = () => {
    setShowDeleteConfirmationPopup(true);
  };

  const handleDeleteAction = () => {
    setShowDeleteConfirmationPopup(false);
    if (onDeleteNode && nodeId) {
      onDeleteNode(nodeId);
    }
  };

  const hideOnBlur = () => {
    setShowDeleteConfirmationPopup(false);
  };

  const handleDoubleClick = () => {
    if (!isReadOnly && !isCompleted) {
      setInEdit(true);
    }
  };

  const enrichAssignmentData = async (rawData) => {
    let taskAssignment = null;
    let workflowAssignment = null;

    // Lookup task assignment (user or project)
    if (rawData.taskAssignmentType === 2) {
      // User
      taskAssignment = defaultDetails?.assigneeList?.find((u) => u.value === rawData.taskAssignmentId);
    } else if (rawData.taskAssignmentType === 3) {
      // Project
      taskAssignment = defaultDetails?.allProjectsList?.find((p) => p.value === rawData.taskAssignmentId || p.ProjectId === rawData.taskAssignmentId);
    }

    // Lookup workflow assignment
    if (taskAssignment && defaultDetails) {
      const workflowList = await getDD4List({
        assignedId: { ...taskAssignment, AssignmentType: rawData.taskAssignmentType },
        defaultDetails,
      });
      workflowAssignment = rawData.workflowAssignmentId ? workflowList?.find((w) => w.value === rawData.workflowAssignmentId) : {label : label.UNASSIGNED, RelatedAssignmentType: 2};
    }

    // Set enriched data
    setEnrichedData({
      ...rawData,
      taskAssignmentId: taskAssignment || { label: "Not Selected" },
      workflowAssignmentId: workflowAssignment || { label: "Not Selected" },
    });
  };

  useEffect(() => {
    if (!assignmentData) {
      // Don't clear enrichedData immediately - keep the existing data
      // Only clear if we explicitly want to reset
      return;
    }

    enrichAssignmentData(assignmentData);
  }, [assignmentData, defaultDetails]);

  return (
    <div className='assignment-display' onDoubleClick={handleDoubleClick}>
      <div className='assignment-header justify-content-between'>
        <div className='d-flex align-items-center'>
          {icon.USER_CHECK}
          <span className='assignment-title ml-2'>Assign</span>
        </div>
        {!isReadOnly && !isCompleted && (
          <Tooltip position='bottom' anchorElement='target' parentTitle={true}>
            <div className='header-actions d-flex align-items-center gap-2'>
              <span className='assignment-display-edit-icon' onClick={() => setInEdit(true)}>
                {icon.EDIT_ICON}
              </span>
              <span ref={deleteIconRef} className={`assignment-display-delete-icon ${showDeleteConfirmationPopup ? "icon-active" : ""}`} onClick={handleDelete} title='Delete'>
                {icon.DELETE_ICON}
              </span>
            </div>
          </Tooltip>
        )}
      </div>

      <div className='assignment-content'>
        {enrichedData ? (
          <>
            {enrichedData.dynamicFlow || enrichedData.flowElementType === 2 ? (
              // Dynamic Flow: Show only variable name and profile type
              <div className='assignment-item'>
                <span className='item-icon'>{icon.FORMS}</span>
                <span className='item-label'>
                  {enrichedData.selectedActiveMember?.name || enrichedData.selectedActiveMember?.label || "Dynamic Assignment"} (
                  {enrichedData.profileType === 1 || enrichedData.profileType === "ticketCreator" ? "Ticket Creator" : "Current Assignee"})
                </span>
              </div>
            ) : (
              // Standard Flow: Show task and workflow assignments
              <>
                {/* Show Task Assignment (Project/User) */}
                <div className='assignment-item'>
                  <span className='item-icon'>
                    {enrichedData.taskAssignmentId?.AssignmentType === 3 ? icon["USERS_ADD"] : icon["PERSON_SINGLE"]}
                  </span>
                  <span className='item-label'>{enrichedData.taskAssignmentId?.label || "Not assigned"}</span>
                </div>

                {/* Show Workflow Assignment (Workflow/User) */}
                <div className='assignment-item'>
                  <span className='item-icon'>
                    {enrichedData.workflowAssignmentId?.RelatedAssignmentType === 5 ? icon["CIRCUIT_BOARD"] : icon["PERSON_SINGLE"]}
                  </span>
                  <span className='item-label'>{enrichedData.workflowAssignmentId?.label || "Not assigned"}</span>
                </div>
              </>
            )}

            {/* Show badges for approval/lock (for both dynamic and standard) */}
            {(enrichedData.isApproval || enrichedData.isFlowLocked) && (
              <Tooltip position='bottom'>
                <div className='assignment-badges'>
                  {enrichedData.isApproval && (
                    <span className='badge badge-warning'>
                      <span title='Approval Requested' className='assignment-display-approval-icon'>
                        {icon.APPROVAL_ICON}
                      </span>
                    </span>
                  )}
                  {enrichedData.isFlowLocked && (
                    <span className='badge badge-secondary'>
                      <span title='Task Locked' className='assignment-display-lock-icon'>
                        {icon.LOCK_ICON}
                      </span>
                    </span>
                  )}
                </div>
              </Tooltip>
            )}
          </>
        ) : (
          <div className='assignment-item text-muted'>
            <span className='lucid-icon-common item-icon'>info</span>
            <span className='item-label'>Click edit to configure assignment</span>
          </div>
        )}
      </div>

      {showDeleteConfirmationPopup && (
        <NodeDeleteConfirmPopup
          anchor={deleteIconRef.current}
          deleteRef={deleteRef}
          blurTimeoutRef={blurTimeoutRef}
          onConfirm={handleDeleteAction}
          onCancel={hideOnBlur}
          hideOnBlur={hideOnBlur}
        />
      )}
    </div>
  );
};

export default AssignmentDisplay;
